// src/components/CloseCashModal.js
import { useState } from "react";
import { fmtCLP } from "../utils/formato";
import "./CloseCashModal.css";

const N = (v) => Number(v || 0);
const DENOMINACIONES = [
  { valor: 20000, label: "$20.000", tipo: "Billetes" },
  { valor: 10000, label: "$10.000", tipo: "Billetes" },
  { valor: 5000, label: "$5.000", tipo: "Billetes" },
  { valor: 2000, label: "$2.000", tipo: "Billetes" },
  { valor: 1000, label: "$1.000", tipo: "Billetes" },
  { valor: 500, label: "$500", tipo: "Monedas" },
  { valor: 100, label: "$100", tipo: "Monedas" },
  { valor: 50, label: "$50", tipo: "Monedas" },
  { valor: 10, label: "$10", tipo: "Monedas" },
];

export default function CloseCashModal({ cierre, resumen, apertura = null, movimientos = [], onCerrar, cargando, onConfirmar }) {
  const [cantidades, setCantidades] = useState(() =>
    DENOMINACIONES.reduce((acc, d) => ({ ...acc, [d.valor]: "" }), {})
  );
  const desde = cierre?.desde || resumen?.desde;
  const hasta = cierre?.hasta || resumen?.hasta;
  const mostrandoResumen = !cierre && resumen;
  const totalIngresos = movimientos.filter((x) => x.tipo === "ingreso").reduce((s, x) => s + N(x.monto), 0);
  const totalEgresos = movimientos.filter((x) => x.tipo === "egreso").reduce((s, x) => s + N(x.monto), 0);
  const fondoInicial = N(apertura?.monto_total);
  const efectivoVentas = N(resumen?.total_efectivo);
  const efectivoEsperado = cierre?.efectivo_esperado != null
    ? N(cierre.efectivo_esperado)
    : fondoInicial + efectivoVentas + totalIngresos - totalEgresos;
  const desgloseEfectivo = DENOMINACIONES.reduce((acc, d) => {
    const cantidad = parseInt(cantidades[d.valor], 10) || 0;
    if (cantidad > 0) acc[d.valor] = cantidad;
    return acc;
  }, {});
  const contadoIngresado = cierre || DENOMINACIONES.some((d) => String(cantidades[d.valor] ?? "").trim() !== "");
  const efectivoContadoCalculado = DENOMINACIONES.reduce((sum, d) => {
    const cantidad = parseInt(cantidades[d.valor], 10) || 0;
    return sum + d.valor * cantidad;
  }, 0);
  const efectivoContado = cierre?.efectivo_contado != null ? N(cierre.efectivo_contado) : efectivoContadoCalculado;
  const diferenciaEfectivo = cierre?.diferencia_efectivo != null
    ? N(cierre.diferencia_efectivo)
    : efectivoContado - efectivoEsperado;
  const diferenciaClass = diferenciaEfectivo === 0 ? "ok" : diferenciaEfectivo > 0 ? "sobrante" : "faltante";
  const diferenciaLabel = diferenciaEfectivo === 0 ? "Cuadrado" : diferenciaEfectivo > 0 ? "Sobra" : "Falta";
  const cambiarCantidad = (valor, value) => {
    const limpio = value.replace(/\D/g, "").slice(0, 5);
    setCantidades((prev) => ({ ...prev, [valor]: limpio }));
  };


  return (
    <div className="cc-overlay">
      <div className="cc-modal cc-modal--wide">
        <h2 className="cc-title">Cerrar caja</h2>

        {!cierre && !resumen && (
          <p className="cc-text">
            Cargando resumen del cierre...
          </p>
        )}

        {resumen && (
          <>
            <p className="cc-text">
              {mostrandoResumen
                ? "Resumen informativo de lo que se cerrará:"
                : "Cierre generado para el período:"}
            </p>
            {desde && hasta && (
              <p className="cc-periodo">
                {new Date(desde).toLocaleString("es-CL")} —{" "}
                {new Date(hasta).toLocaleString("es-CL")}
              </p>
            )}

            {apertura != null && (
              <>
                <h3 className="cc-subtitle">Inicio de caja</h3>
                <div className="cc-grid">
                  <div>
                    <div className="cc-label">Fondo inicial</div>
                    <div className="cc-value">{fmtCLP(apertura.monto_total)}</div>
                  </div>
                </div>
              </>
            )}

            <div className="cc-grid">
              <div>
                <div className="cc-label">Total pedidos</div>
                <div className="cc-value">{resumen.total_pedidos}</div>
              </div>
              <div>
                <div className="cc-label">Ventas netas</div>
                <div className="cc-value">{fmtCLP(resumen.total_neto)}</div>
              </div>
            </div>

            <h3 className="cc-subtitle">Por medio de pago</h3>
            <div className="cc-grid">
              <div>
                <div className="cc-label">Efectivo</div>
                <div className="cc-value">{fmtCLP(resumen.total_efectivo)}</div>
              </div>
              <div>
                <div className="cc-label">Transferencia</div>
                <div className="cc-value">{fmtCLP(resumen.total_transfer)}</div>
              </div>
              <div>
                <div className="cc-label">Tarjeta</div>
                <div className="cc-value">{fmtCLP(resumen.total_tarjeta)}</div>
              </div>
              <div>
                <div className="cc-label">Otro</div>
                <div className="cc-value">{fmtCLP(resumen.total_otro)}</div>
              </div>
            </div>

            {Array.isArray(movimientos) && movimientos.length > 0 && (
              <>
                <h3 className="cc-subtitle">Movimientos del turno (para cuadrar caja)</h3>
                <div className="cc-movimientos-wrap">
                  <table className="cc-movimientos-table">
                    <thead>
                      <tr>
                        <th>Hora</th>
                        <th>Tipo</th>
                        <th>Concepto</th>
                        <th className="right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimientos.map((m) => (
                        <tr key={m.id}>
                          <td>{new Date(m.creado_en).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}</td>
                          <td>{m.tipo === "ingreso" ? "Ingreso" : "Egreso"}</td>
                          <td>{(m.concepto || "—").slice(0, 30)}</td>
                          <td className="right">{m.tipo === "ingreso" ? "+ " : "− "}{fmtCLP(m.monto)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}>Total ingresos</td>
                        <td className="right">{fmtCLP(movimientos.filter((x) => x.tipo === "ingreso").reduce((s, x) => s + Number(x.monto || 0), 0))}</td>
                      </tr>
                      <tr>
                        <td colSpan={3}>Total egresos</td>
                        <td className="right">{fmtCLP(movimientos.filter((x) => x.tipo === "egreso").reduce((s, x) => s + Number(x.monto || 0), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}

            <div className="cc-cuadre">
              <h3 className="cc-subtitle">Cuadre efectivo</h3>
              <p className="cc-cuadre-formula">
                Fondo inicial + Efectivo ventas + Ingresos − Egresos = Efectivo esperado
              </p>
              <p className="cc-cuadre-valores">
                {fmtCLP(fondoInicial)} + {fmtCLP(efectivoVentas)} + {fmtCLP(totalIngresos)} − {fmtCLP(totalEgresos)} ={" "}
                <strong>
                  {fmtCLP(efectivoEsperado)}
                </strong>
              </p>
            </div>

            <div className="cc-arqueo">
              <h3 className="cc-subtitle">Efectivo contado</h3>
              {!cierre ? (
                <>
                  <div className="cc-denom-wrap">
                    {["Billetes", "Monedas"].map((tipo) => (
                      <div className="cc-denom-section" key={tipo}>
                        <div className="cc-denom-title">{tipo}</div>
                        <div className="cc-denom-grid">
                          {DENOMINACIONES.filter((d) => d.tipo === tipo).map((d) => {
                            const cantidad = parseInt(cantidades[d.valor], 10) || 0;
                            return (
                              <label className="cc-denom-row" key={d.valor}>
                                <span className="cc-denom-label">{d.label}</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  inputMode="numeric"
                                  value={cantidades[d.valor]}
                                  onChange={(e) => cambiarCantidad(d.valor, e.target.value)}
                                  disabled={cargando}
                                  aria-label={`Cantidad ${d.label}`}
                                />
                                <span className="cc-denom-subtotal">{fmtCLP(d.valor * cantidad)}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="cc-arqueo-total cc-arqueo-total--input">
                    <span>Total contado</span>
                    <strong>{fmtCLP(efectivoContado)}</strong>
                  </div>
                </>
              ) : (
                <div className="cc-arqueo-total">
                  <span>Contado</span>
                  <strong>{fmtCLP(efectivoContado)}</strong>
                </div>
              )}

              {(contadoIngresado || cierre) && (
                <div className={`cc-diferencia ${diferenciaClass}`}>
                  <span>{diferenciaLabel}</span>
                  <strong>{fmtCLP(Math.abs(diferenciaEfectivo))}</strong>
                </div>
              )}
            </div>
          </>
        )}

        <div className="cc-actions">
          <button className="cc-btn secondary" onClick={onCerrar} disabled={cargando}>
            Cerrar
          </button>

          {!cierre && (
            <button
              className="cc-btn primary"
              onClick={() => onConfirmar({ efectivo_contado: efectivoContado, desglose_efectivo: desgloseEfectivo })}
              disabled={cargando || !resumen || !contadoIngresado}
            >
              {!resumen ? "Cargando..." : cargando ? "Generando cierre..." : !contadoIngresado ? "Ingresa efectivo contado" : "Confirmar cierre"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
